import {
  AnimatedModal,
  EHeadingStyleVariant,
  EModalSize,
  EModalVariants,
  Heading,
  LinkButton,
  Modal,
} from '@outdoorsyco/bonfire';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useBreakpoint } from '@/hooks/useBreakpoint';

interface IClampModalProps {
  modalContent?: React.ReactNode;
  lineCount?: number;
  showMoreText?: string;
  modalTitle?: string;
  hasMore?: boolean;
  ellipsis?: boolean;
  hideOverflowContent?: boolean;
  // Rendered alongside the "Show more" link AND at the top of the modal,
  // so a caller can attach domain-specific badges/CTAs to both surfaces
  // without coupling ClampModal to that domain.
  accessory?: React.ReactNode;
}

const ClampModal: React.FC<React.PropsWithChildren<IClampModalProps>> = ({
  modalContent,
  children,
  lineCount = 3,
  showMoreText,
  modalTitle,
  hasMore,
  ellipsis,
  hideOverflowContent = false,
  accessory,
}) => {
  const [shouldShowAll, setShouldShowAll] = useState(false);
  const [open, setOpen] = useState(false);
  const [showMoreButton, setShowMoreButton] = useState(true);
  const content = useRef<HTMLDivElement>(null);
  const renderedContent = useRef<HTMLDivElement>(null);
  const fullHeight = content.current?.scrollHeight || 0;
  const trimmedHeight = renderedContent.current?.offsetHeight || 0;

  useEffect(() => {
    if (hasMore === undefined && fullHeight > trimmedHeight) {
      setShowMoreButton(true);
    } else {
      setShowMoreButton(false);
    }
  }, [fullHeight, trimmedHeight, lineCount, hasMore]);

  const intl = useIntl();
  const { isMobile } = useBreakpoint();

  const showMore = intl.formatMessage({
    defaultMessage: 'Show more',
    id: 'aWpBzj',
  });

  const handleToggleAll = (event: React.MouseEvent) => {
    event.preventDefault();
    setOpen(true);
  };

  useEffect(() => {
    if (shouldShowAll) {
      if (isMobile) {
        setOpen(true);
        setShouldShowAll(false);
      } else {
        setOpen(false);
        setShouldShowAll(true);
      }
    }
  }, [isMobile, shouldShowAll]);

  return (
    <>
      {!hideOverflowContent && (
        <div ref={content} className="h-0 overflow-hidden">
          {children}
        </div>
      )}
      <div ref={renderedContent} className={ellipsis ? `line-clamp-${lineCount}` : ''}>
        {children}
      </div>
      {(hasMore || showMoreButton || accessory) && (
        <div className="flex flex-wrap items-center justify-between mt-2 gap-3 md:col-span-10">
          {accessory}
          {(hasMore || showMoreButton) && (
            <LinkButton href="#" onClick={handleToggleAll} label={showMoreText || showMore} />
          )}
        </div>
      )}
      <AnimatedModal
        variant={isMobile ? EModalVariants.Bottom : undefined}
        size={EModalSize.Medium}
        isOpen={open}
        onClose={() => setOpen(false)}>
        <Modal.Actions />
        <Modal.Header inlineHeader>
          <Heading variant={EHeadingStyleVariant.H6}>{modalTitle}</Heading>
        </Modal.Header>
        <Modal.Content>
          {accessory && <div className="mb-6">{accessory}</div>}
          {modalContent ?? children}
        </Modal.Content>
      </AnimatedModal>
    </>
  );
};

export default ClampModal;
